/**
 * Matching what people actually type into a search box.
 *
 * `name.toLowerCase().includes(query.toLowerCase())` fails on two things that
 * happen constantly in a Romanian club roster:
 *
 *   - diacritics. A roster holds "Ștefan Mureșan"; the coach types "stefan"
 *     on a phone keyboard that does not offer Ș, and finds nobody.
 *   - word order. The roster holds "Mureșan Ștefan" because that is how the
 *     club entered it; the coach types "stefan muresan" and finds nobody.
 *
 * Both looked like "search is broken", and from the coach's side it was.
 */

/**
 * Lowercase, strip accents, collapse whitespace.
 *
 * NFD splits a letter into its base plus its combining marks, which the range
 * below then removes — so ș, ț, ă, â, î all fold onto their plain letters, as
 * do the German and French ones a sponsor's name might carry.
 */
export function normalizeForSearch(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    // Romanian ș/ț are often typed with a cedilla rather than a comma below;
    // NFD leaves those as-is on some platforms, so fold them explicitly.
    .replace(/[șşŞȘ]/g, "s")
    .replace(/[țţŢȚ]/g, "t")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * True when every word of the query appears somewhere in the haystack.
 *
 * Every word, in any order, each one a prefix-or-substring match. "stefan mur"
 * finds "Mureșan Ștefan"; "stefan ionescu" does not, because the second word
 * has to appear too — an AND, so adding a word always narrows.
 *
 * An empty query matches everything: a search box with nothing in it is not a
 * filter.
 */
export function matchesQuery(query: string, ...fields: unknown[]): boolean {
  const terms = normalizeForSearch(query).split(" ").filter(Boolean);
  if (terms.length === 0) return true;

  const haystack = fields.map(normalizeForSearch).filter(Boolean).join(" ");
  if (!haystack) return false;

  return terms.every((term) => haystack.includes(term));
}
